import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  CheckCircle2, AlertCircle, ArrowLeft, ArrowRight, Send, 
  Star, Upload, Calendar as CalendarIcon, ChevronsUpDown,
  Type, AlignLeft, Mail, Phone, List, ToggleLeft
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const FormView = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(window.location.search);
    const opportunityId = queryParams.get('opportunityId');
    
    const [form, setForm] = useState(null);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const startTime = useRef(null);
    const viewCounted = useRef(false);

    useEffect(() => {
        const fetchForm = async () => {
            try {
                const res = await fetch(`/api/forms/v/${slug}`);
                const data = await res.json();
                if (res.ok && data.success) {
                    setForm(data.form);
                    startTime.current = Date.now(); // Start timer when form is loaded
                    // Initialize answers with empty strings or default values
                    const initialAnswers = {};
                    data.form.questions.forEach(q => {
                        if (q.type === 'checkbox') initialAnswers[q.id] = [];
                        else if (q.type === 'rating') initialAnswers[q.id] = 0;
                        else initialAnswers[q.id] = '';
                    });
                    setAnswers(initialAnswers);
                } else {
                    setError(data.message || "Form not found");
                }
            } catch (err) {
                console.error(err);
                setError("Failed to load form. Check your internet connection.");
            } finally {
                setLoading(false);
            }
        };
        fetchForm();
    }, [slug]);

    // Track view exactly once per component mount
    useEffect(() => {
        const trackView = async () => {
            if (viewCounted.current) return;
            viewCounted.current = true;
            try {
                await fetch(`/api/forms/v/${slug}/view`, { method: 'POST' });
            } catch (err) {
                console.error("Failed to track view:", err);
            }
        };
        trackView();
    }, [slug]);

    const handleAnswerChange = (qId, value) => {
        setAnswers(prev => ({ ...prev, [qId]: value }));
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        
        // Validate required questions
        const missingRequired = form.questions.filter(q => q.required && !answers[q.id]);
        if (missingRequired.length > 0) {
            setError(`Please answer all required questions.`);
            return;
        }

        setIsSubmitting(true);
        setError(null);

        const timeSpentSeconds = Math.round((Date.now() - startTime.current) / 1000);

        try {
            const res = await fetch(`/api/forms/${form.id}/responses`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    respondentName: "Anonymous", // Optional: collect name from form if needed
                    answers,
                    timeSpent: timeSpentSeconds,
                    completed: true
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setSubmitted(true);
            } else {
                setError(data.message || "Failed to submit response");
            }
        } catch (err) {
            console.error(err);
            setError("Network error. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                    <p className="text-sm font-bold text-muted-foreground animate-pulse">Loading form details...</p>
                </div>
            </div>
        );
    }

    if (error && !form) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6 text-center">
                <div className="max-w-md space-y-6">
                    <div className="w-20 h-20 bg-rose-500/10 rounded-3xl flex items-center justify-center mx-auto text-rose-500">
                        <AlertCircle size={40} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-foreground">Something went wrong</h1>
                        <p className="text-sm text-muted-foreground mt-2">{error}</p>
                    </div>
                    <button onClick={() => navigate(-1)} className="h-11 px-8 rounded-full bg-primary text-primary-foreground font-bold shadow-lg shadow-primary/20">
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6 text-center">
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-md space-y-8 p-10 bg-card rounded-[40px] border border-border shadow-2xl relative overflow-hidden"
                >
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-400 to-teal-500" />
                    
                    <div className="w-20 h-20 bg-emerald-500/10 rounded-3xl flex items-center justify-center mx-auto text-emerald-500">
                        <CheckCircle2 size={40} />
                    </div>
                    
                    <div>
                        <h1 className="text-3xl font-black text-foreground tracking-tight">Response Submitted</h1>
                        <p className="text-sm font-medium text-muted-foreground mt-3 leading-relaxed">
                            {opportunityId 
                                ? "Part 1 complete! Now return to finish your application by uploading your CV and cover letter." 
                                : "Thank you for your response! Your participation has been recorded successfully."}
                        </p>
                    </div>
                    
                    <button 
                        onClick={() => navigate(opportunityId ? "/user?tab=opps" : "/")} 
                        className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-bold text-base shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all active:scale-95"
                    >
                        {opportunityId ? "Finish Application" : "Return Home"}
                    </button>
                    
                    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Powered by YeniDers Network</p>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground pb-20 selection:bg-primary/20">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-12 sm:pt-24">
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-card rounded-[32px] sm:rounded-[48px] border border-border p-8 sm:p-14 shadow-2xl shadow-black/5 relative overflow-hidden"
                >
                    <div className="absolute top-0 left-0 w-full h-3 bg-gradient-to-r from-primary via-purple-500 to-blue-500" />
                    
                    <div className="mb-14">
                        <h1 className="text-3xl sm:text-4xl font-display font-black text-foreground tracking-tight leading-tight">{form.title}</h1>
                        {form.description && <p className="text-base font-medium text-muted-foreground mt-5 leading-relaxed opacity-80">{form.description}</p>}
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-12">
                        {form.questions.map((q, i) => (
                            <div key={q.id} className="space-y-5">
                                <label className="block text-lg font-bold text-foreground leading-snug">
                                    <span className="text-primary font-mono text-sm mr-2 opacity-30">{i + 1}.</span>
                                    {q.text}
                                    {q.required && <span className="text-rose-500 ml-1.5 font-black">*</span>}
                                </label>

                                <div className="pl-0 sm:pl-7">
                                    {q.type === "short_text" && (
                                        <input 
                                            required={q.required}
                                            value={answers[q.id] || ''}
                                            onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                            placeholder="Your answer..."
                                            className="w-full h-12 border-b-2 border-border/60 bg-transparent px-2 text-base font-semibold focus:outline-none focus:border-primary transition-all placeholder:text-muted-foreground/30"
                                        />
                                    )}

                                    {q.type === "long_text" && (
                                        <textarea 
                                            required={q.required}
                                            value={answers[q.id] || ''}
                                            onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                            placeholder="Detailed response..."
                                            rows={4}
                                            className="w-full rounded-2xl border-2 border-border/60 bg-transparent p-4 text-base font-semibold focus:outline-none focus:border-primary transition-all resize-none shadow-sm"
                                        />
                                    )}

                                    {q.type === "email" && (
                                        <input 
                                            type="email"
                                            required={q.required}
                                            value={answers[q.id] || ''}
                                            onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                            placeholder="email@example.com"
                                            className="w-full h-12 border-b-2 border-border/60 bg-transparent px-2 text-base font-semibold focus:outline-none focus:border-primary"
                                        />
                                    )}

                                    {q.type === "phone" && (
                                        <input 
                                            type="tel"
                                            required={q.required}
                                            value={answers[q.id] || ''}
                                            onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                            placeholder="+994 (50) 000-00-00"
                                            className="w-full h-12 border-b-2 border-border/60 bg-transparent px-2 text-base font-semibold focus:outline-none focus:border-primary"
                                        />
                                    )}

                                    {(q.type === "multiple_choice" || q.type === "dropdown") && (
                                        <div className="space-y-3">
                                            {q.options?.map((opt, idx) => (
                                                <label 
                                                    key={idx} 
                                                    className={`
                                                        flex items-center gap-4 p-4 rounded-2xl border-2 transition-all cursor-pointer group
                                                        ${answers[q.id] === opt.label ? "bg-primary/5 border-primary" : "bg-muted/10 border-transparent hover:border-border"}
                                                    `}
                                                >
                                                    <input 
                                                        type="radio" 
                                                        name={q.id}
                                                        checked={answers[q.id] === opt.label}
                                                        onChange={() => handleAnswerChange(q.id, opt.label)}
                                                        className="hidden"
                                                    />
                                                    <div className={`
                                                        w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
                                                        ${answers[q.id] === opt.label ? "border-primary" : "border-muted-foreground/30 group-hover:border-primary/50"}
                                                    `}>
                                                        {answers[q.id] === opt.label && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                                                    </div>
                                                    <span className={`text-sm font-bold transition-all ${answers[q.id] === opt.label ? "text-foreground" : "text-muted-foreground"}`}>{opt.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}

                                    {q.type === "checkbox" && (
                                        <div className="space-y-3">
                                            {q.options?.map((opt, idx) => {
                                                const isChecked = (answers[q.id] || []).includes(opt.label);
                                                return (
                                                    <label 
                                                        key={idx} 
                                                        className={`
                                                            flex items-center gap-4 p-4 rounded-2xl border-2 transition-all cursor-pointer group
                                                            ${isChecked ? "bg-primary/5 border-primary" : "bg-muted/10 border-transparent hover:border-border"}
                                                        `}
                                                    >
                                                        <input 
                                                            type="checkbox" 
                                                            checked={isChecked}
                                                            onChange={(e) => {
                                                                const current = answers[q.id] || [];
                                                                if (e.target.checked) handleAnswerChange(q.id, [...current, opt.label]);
                                                                else handleAnswerChange(q.id, current.filter(val => val !== opt.label));
                                                            }}
                                                            className="hidden"
                                                        />
                                                        <div className={`
                                                            w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all
                                                            ${isChecked ? "bg-primary border-primary" : "border-muted-foreground/30 group-hover:border-primary/50"}
                                                        `}>
                                                            {isChecked && <CheckCircle2 size={12} className="text-white" />}
                                                        </div>
                                                        <span className={`text-sm font-bold transition-all ${isChecked ? "text-foreground" : "text-muted-foreground"}`}>{opt.label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {q.type === "rating" && (
                                        <div className="flex gap-3">
                                            {Array.from({ length: q.ratingMax || 5 }).map((_, rIdx) => {
                                                const val = rIdx + 1;
                                                const isActive = answers[q.id] >= val;
                                                return (
                                                    <button
                                                        key={rIdx}
                                                        type="button"
                                                        onClick={() => handleAnswerChange(q.id, val)}
                                                        className={`transition-all duration-300 transform ${isActive ? "text-amber-400 scale-110" : "text-muted-foreground/20 hover:text-amber-300"}`}
                                                    >
                                                        <Star size={36} fill={isActive ? "currentColor" : "none"} strokeWidth={1.5} />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {q.type === "date_picker" && (
                                        <input 
                                            type="date"
                                            required={q.required}
                                            value={answers[q.id] || ''}
                                            onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                            className="h-12 rounded-xl border-2 border-border/60 bg-transparent px-4 text-base font-semibold focus:outline-none focus:border-primary"
                                        />
                                    )}
                                </div>
                            </div>
                        ))}

                        <div className="pt-8 border-t border-border mt-12">
                            {error && (
                                <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-400 text-sm font-bold flex items-center gap-3">
                                    <AlertCircle size={18} /> {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`
                                    w-full h-14 rounded-2xl bg-primary text-primary-foreground font-bold text-lg shadow-lg transition-all flex items-center justify-center gap-3
                                    ${isSubmitting ? "opacity-70 cursor-not-allowed" : "hover:shadow-primary/30 active:scale-[0.98] shadow-primary/20"}
                                `}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        Submit Form <Send size={18} />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                    
                    <div className="mt-16 flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">
                        <span>Safe & Encrypted Submission</span>
                        <span>YeniDers Network</span>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default FormView;
